import React, { createContext, useContext, useState, useEffect } from 'react';
import { formatDateSafe, normalizeLocale } from '../utils/formatUtils';

const TimezoneContext = createContext();

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

// [NOTIF-2026-05-21] Multi-country model:
//  • Storage: всегда UTC (бэк хранит абсолютный момент).
//  • Display: BROWSER LOCAL — сотрудник видит время по СВОИМ часам (так же
//    как в Gmail/Google Calendar). Никаких `timeZone:` override — Intl
//    использует разрешённый браузерный tz по умолчанию.
//  • Bell: фуllет когда сервер-UTC = reminder-UTC → сотрудник видит
//    уведомление ровно в момент своего «настенного» времени.
const TIMEZONES = {
  'auto': { label: 'Местное время (браузер)', offset: 'auto' },
};

export const TimezoneProvider = ({ children }) => {
  const [selectedTimezone] = useState('auto');

  useEffect(() => {
    localStorage.setItem('selectedTimezone', 'auto');
  }, []);

  // No `timeZone:` override → Intl picks browser's resolved tz.
  const formatDateTime = (dateString, options = {}) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return formatDateSafe(date, 'ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      ...options,
    });
  };

  const formatDate = (dateString, options = {}) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return formatDateSafe(date, 'ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...options,
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return formatDateSafe(date, 'ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCurrentTime = () => {
    // [NOTIF-2026-05-21] Browser local — no timeZone override.
    try {
      const now = new Date();
      return formatDateSafe(now, 'ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return new Date().toLocaleString();
    }
  };

  const getCurrentTimeISO = () => {
    // [NOTIF-2026-05-21] Browser local YYYY-MM-DDTHH:MM:SS.
    try {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch (error) {
      return new Date().toISOString().slice(0, 19);
    }
  };

  const getTimezoneInfo = () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
      const offset = getTimezoneOffset();
      return { timezone: tz, name: tz, offset, abbreviation: '' };
    } catch (error) {
      return { timezone: 'local', name: 'local', offset: '+00:00', abbreviation: '' };
    }
  };

  const getTimezoneOffset = () => {
    try {
      const m = -new Date().getTimezoneOffset();
      const sign = m >= 0 ? '+' : '-';
      const abs = Math.abs(m);
      return `${sign}${String(Math.floor(abs/60)).padStart(2,'0')}:${String(abs%60).padStart(2,'0')}`;
    } catch (error) {
      return '+00:00';
    }
  };

  const value = {
    selectedTimezone,
    setSelectedTimezone: () => {}, // Dummy function - timezone is fixed
    timezones: TIMEZONES,
    formatDateTime,
    formatDate,
    formatTime,
    getCurrentTime,
    getCurrentTimeISO,
    getTimezoneOffset,
    getTimezoneInfo
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

export default TimezoneContext;